import * as React from 'react';
import { useEffect, useState } from 'react';
import { Flashbar, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { dissociateTemplateFromResources, getTemplate, listTemplates } from 'src/api/permissions';
import {
  getUsageAgreementModal,
  isAccessRequestWorkflowType,
  TemplateDetailsComponent,
} from 'src/components/templates/common';
import { AssociateTemplateModal } from 'src/components/templates/associateTemplateModal';
import { TABLE_CONTENT_TYPE, TEMPLATE_NONE, TEMPLATE_TYPE_ACCESS_MANAGEMENT } from 'src/commons/constants';
import { isGlobalTemplate } from 'src/components/utils/template';
import { ResourceNotFound } from 'src/commons/ResourceNotFound';

import { PageHeader } from 'src/components/common/PageHeader';
import { Page } from 'src/routes';
import TemplateResourcesTable from 'src/components/templates/templateResourcesTable';
import { getDataAgreement } from 'src/api/d3a';
import { isD3AServiceAvailable } from 'src/api/config';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface TemplateDetailsProps extends CommonPageProps {}

export const TemplateView = (props: TemplateDetailsProps) => {
  const [template, setTemplate] = useState({});
  const [associateModalVisible, setAssociateModalVisible] = useState(false);
  const [templateId] = useState(props.match.params.templateId);
  const [notifications, setNotifications] = useState([]);
  const [templateResources, setTemplateResources] = useState([]);
  const [tableMessage, setTableMessage] = useState('No resources!');
  const [arwDataAgreement, setArwDataAgreement] = useState(null);
  const [arwDataAgreementModalVisible, setArwDataAgreementModalVisible] = useState(false);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, [props.activeGroup, props.activeWorkspace]);

  const handleRefresh = async () => {
    if (!props.activeGroup || !props.match.params.templateId || !props.match.params.resourceId) return;
    try {
      const templateResult = await getTemplate({
        templateId: props.match.params.templateId,
        resourceId: props.match.params.resourceId,
      });
      const template = templateResult.template;
      setTemplate(template);
      // fetch associated resources and data agreement
      if (template !== undefined) {
        const ownerId = props.activeWorkspace ? props.activeWorkspace.workspaceId : props.activeGroup;
        let listTemplatesResult = await listTemplates({
          ownerId: ownerId,
          templateType: template.templateType,
        });
        if (listTemplatesResult && listTemplatesResult.templates) {
          setTemplateResources(
            listTemplatesResult.templates.filter((t) => templateId == t.templateId && t.resourceId != TEMPLATE_NONE),
          );
        }
        if (
          isAccessRequestWorkflowType(template) &&
          !!template.accessRequestWorkflows?.[0]?.dataAgreement?.agreementId
        ) {
          const agreementId = template.accessRequestWorkflows?.[0]?.dataAgreement?.agreementId;
          getDataAgreement({ id: agreementId }).then(
            (apiResp) => {
              setArwDataAgreement(apiResp.dataAgreements[0]);
            },
            (err) => {
              console.error(`Failed to get data agreement with id: ${agreementId}`, err);
              setArwDataAgreement(null);
            },
          );
        }
      }
    } catch (err) {
      setTemplate(null);
      setTableMessage(`Unable to load template associations. ${err.message}`);
    }
  };

  const handleEditTemplate = async () => {
    if (template) {
      props.setRedirect(Page.EDIT_TEMPLATE, false, {
        template: template,
      });
    } else {
      handleNotification('Cannot edit', 'Please select a template to edit');
    }
  };

  const handleDissociateResource = async (dissociateTemplateId, dissociateResourceId) => {
    try {
      await dissociateTemplateFromResources({
        templateId: dissociateTemplateId,
        resourceId: dissociateResourceId,
      });
      setTemplateResources(
        templateResources.filter((r) => {
          return r.resourceId != dissociateResourceId;
        }),
      );
      handleNotification('success', 'Template resource dissociated successfully.');
    } catch (err) {
      handleNotification('error', 'Failed to dissociate the template.');
    }
  };

  const handleNotification = (type, message) => {
    setNotifications([
      {
        type: type,
        content: message,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };

  if (!template) {
    return (
      <ResourceNotFound
        title={'Template not found'}
        subtitle={
          `The given template ID ${props.match.params.templateId} is not valid, or you do not have permission to view it. Please\n` +
          ' check the URL for mistakes and try again.'
        }
      />
    );
  }

  const templateDetailsButtons = [];
  if (template) {
    const resourceId = template['resourceId'];
    if (resourceId && resourceId === TEMPLATE_NONE && !isGlobalTemplate(template)) {
      templateDetailsButtons.push({
        text: 'Edit',
        onItemClick: async () => {
          await handleEditTemplate();
        },
      });
      templateDetailsButtons.push({
        text: 'Associate resource',
        variant: 'primary',
        onItemClick: () => {
          setAssociateModalVisible(true);
        },
      });
    }
  }

  return (
    <>
      <Flashbar items={notifications}></Flashbar>
      <SpaceBetween direction='vertical' size='l'>
        <PageHeader buttons={templateDetailsButtons} header={templateId} />
        <TemplateDetailsComponent
          template={template}
          setArwDataAgreementModalVisible={setArwDataAgreementModalVisible}
        />
        {template && (
          <TemplateResourcesTable
            templateResources={templateResources}
            tableMessage={tableMessage}
            notification={handleNotification}
            handleDissociateResource={handleDissociateResource}
          />
        )}
      </SpaceBetween>
      {isD3AServiceAvailable() &&
        !!arwDataAgreement &&
        getUsageAgreementModal(setArwDataAgreementModalVisible, arwDataAgreementModalVisible, arwDataAgreement)}
      <AssociateTemplateModal
        templateId={templateId}
        setNotification={handleNotification}
        close={() => {
          setAssociateModalVisible(false);
        }}
        visible={associateModalVisible}
        activeGroup={props.activeGroup}
        activeWorkspace={props.activeWorkspace}
        handleRefresh={handleRefresh}
        templateType={template ? template['templateType'] : TEMPLATE_TYPE_ACCESS_MANAGEMENT}
      ></AssociateTemplateModal>
    </>
  );
};
